import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DataProvider, { CredentialDocumentItem, DoctorRegisterPayload } from "../../apis";
import { REGISTRATION_PLATFORM_DOCTOR_WEBSITE } from "../../constants/registrationPlatform";
import instance from "../../core/instance";
import { DeleteOutlined, FileOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, notification, Select } from "antd";
import med from "../../assets/images/logohd.png";
import bg from "../../assets/images/bg2.png";
import doc from "../../assets/images/doc4.avif";
import EmailInput from "../../components/form/email-input";
import PasswordInput from "../../components/form/password-input";
import CustomInput from "../../components/form/custom-input";
import { NotificationType } from "../../utils";
import { useAppStore } from "../../store";

function getRegisterErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "response" in error) {
    const ax = (error as { response?: { data?: { message?: string }; status?: number } }).response;
    if (ax?.data?.message) return ax.data.message;
    if (ax?.status === 409) return "Email is already taken.";
  }
  if (error && typeof error === "object" && "message" in error && typeof (error as Error).message === "string") {
    return (error as Error).message;
  }
  return "An error occurred during sign-up. Please try again.";
}

function CredentialDocumentsField({
  files,
  onFilesChange,
  onRemove,
  inputRef,
}: {
  files: File[];
  onFilesChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: (index: number) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-200 bg-gradient-to-br from-slate-50/90 via-white to-[#f0f7fc] p-4 shadow-sm transition-[border-color,box-shadow] hover:border-[#1A94EC]/35 hover:shadow-md">
      <div className="flex gap-3 sm:gap-4">
        <div
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1 ring-slate-100"
          aria-hidden
        >
          <FileOutlined className="text-xl text-[#1A94EC]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800">Credentials (optional)</p>
          <p className="mt-0.5 text-xs text-slate-500 leading-relaxed">
            Upload license or certificates — JPG, PNG, GIF, or PDF. Up to 10MB per file.
          </p>
          <input
            ref={inputRef as React.LegacyRef<HTMLInputElement>}
            type="file"
            multiple
            accept="image/jpeg,image/png,image/gif,application/pdf"
            className="sr-only"
            onChange={onFilesChange}
            aria-label="Upload credential documents"
          />
          <Button
            type="default"
            icon={<PlusOutlined />}
            onClick={() => inputRef.current?.click()}
            className="mt-3 h-10 w-full border-slate-200 bg-white font-medium text-slate-700 shadow-sm hover:border-[#1A94EC]/50 hover:text-[#1A94EC] sm:w-auto"
          >
            Add files
          </Button>
          {files.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {files.map((f, i) => (
                <li
                  key={`${f.name}-${i}-${f.size}`}
                  className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-white px-3 py-2.5 text-sm text-slate-700 shadow-sm"
                >
                  <span className="min-w-0 truncate font-medium" title={f.name}>
                    {f.name}
                  </span>
                  <button
                    type="button"
                    className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-rose-600 transition-colors hover:bg-rose-50"
                    onClick={() => onRemove(i)}
                    aria-label={`Remove ${f.name}`}
                  >
                    <DeleteOutlined className="text-[13px]" aria-hidden />
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function Register() {
  const [api, contextHolder] = notification.useNotification();
  const { setToken, setUser, setDoctor } = useAppStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fullname, setFullname] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [gender, setGender] = useState<"male" | "female">("male");
  const [institution, setInstitution] = useState<string>("");
  const [licenseId, setLicenseId] = useState<string>("");
  const [dob, setDob] = useState<string>("");
  const [speciality, setSpeciality] = useState<string>("");
  const [pendingCredentialFiles, setPendingCredentialFiles] = useState<File[]>([]);
  const credentialInputRefMobile = useRef<HTMLInputElement>(null);
  const credentialInputRefDesktop = useRef<HTMLInputElement>(null);

  const openNotificationWithIcon = (
    type: NotificationType,
    title: string,
    message: string
  ) => {
    api[type]({
      message: title,
      description: message,
    });
  };

  const onCredentialFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    const next: File[] = [];
    for (const f of Array.from(files)) {
      if (f.size > 10 * 1024 * 1024) {
        openNotificationWithIcon("warning", "File too large", `${f.name} exceeds 10MB.`);
        continue;
      }
      next.push(f);
    }
    if (next.length) setPendingCredentialFiles((prev) => [...prev, ...next]);
    e.target.value = "";
  };

  const removeCredentialFile = (index: number) => {
    setPendingCredentialFiles((prev) => prev.filter((_, i) => i !== index));
  };

  async function onSubmit() {
    if (fullname.length < 3) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Enter your full name (at least 3 characters)."
      );
    }
    if (email.length < 5) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Enter a valid email address."
      );
    }
    if (password.length < 8) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Password must be at least 8 characters."
      );
    }
    if (!phoneNumber.trim()) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Enter your phone number."
      );
    }
    if (institution.length < 2) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Enter your institution name."
      );
    }
    if (licenseId.length < 2) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Enter your medical license ID."
      );
    }

    setLoading(true);
    try {
      const payload: DoctorRegisterPayload = {
        fullname: fullname.trim(),
        email: email.trim(),
        password,
        phoneNumber: phoneNumber.trim(),
        gender: gender as "male" | "female",
        institution: institution.trim(),
        licenseId: licenseId.trim(),
        registrationPlatform: REGISTRATION_PLATFORM_DOCTOR_WEBSITE,
      };
      if (dob) payload.dob = dob;
      if (speciality.trim()) payload.speciality = speciality.trim();

      const { data } = await DataProvider.doctorRegister(payload);

      setToken({
        token: data.token,
        refreshToken: data.refreshToken,
      });
      setUser(data.user);

      if (pendingCredentialFiles.length > 0 && data.token) {
        const docs: CredentialDocumentItem[] = [];
        for (const file of pendingCredentialFiles) {
          try {
            const formData = new FormData();
            formData.append("file", file);
            const uploadRes = await instance.post("/upload/img", formData, {
              headers: {
                Authorization: `Bearer ${data.token}`,
                "Content-Type": "multipart/form-data",
              },
            });
            const rd = uploadRes.data as Record<string, unknown>;
            const inner = rd?.data as Record<string, unknown> | undefined;
            const url =
              (typeof inner?.url === "string" && inner.url) ||
              (typeof rd?.url === "string" && rd.url) ||
              (typeof rd?.imageUrl === "string" && rd.imageUrl) ||
              "";
            if (url) {
              docs.push({
                id: Math.random().toString(36).slice(2, 11),
                type: "credential",
                description: file.name || "",
                url,
              });
            }
          } catch {
            // continue with other files
          }
        }
        if (docs.length > 0) {
          try {
            const profRes = await DataProvider.getDoctorProfile();
            const d = profRes.data as { id?: number; status?: string };
            if (d && d.status !== "failed" && typeof d.id === "number") {
              await DataProvider.updateDoctorProfile(d.id, { credentialDocuments: docs });
            }
          } catch {
            // non-blocking
          }
        }
      }

      try {
        const { data: doctorData } = await DataProvider.getDoctorProfile();
        if (doctorData && doctorData.status !== "failed") {
          setDoctor(doctorData);
        }
      } catch {
        // Doctor profile may not exist yet; user is still logged in
      }

      setLoading(false);
      openNotificationWithIcon(
        "success",
        "Account created",
        "Your account is pending verification. You can sign in and complete your profile."
      );
      navigate("/");
    } catch (e) {
      setLoading(false);
      openNotificationWithIcon("error", "Failed", getRegisterErrorMessage(e));
    }
  }

  return (
    <div className="w-full h-full">
      <div
        className="min-h-[100dvh] lg:hidden flex justify-center items-start w-full bg-cover bg-[#0081D4] bg-blend-overlay overflow-x-hidden overflow-y-auto py-4 px-4 pb-8"
        style={{ backgroundImage: `url(${bg})` }}
      >
        <div className="relative w-full max-w-2xl shrink-0 rounded-2xl border border-white/60 bg-white/95 shadow-xl shadow-slate-900/10 backdrop-blur-sm flex flex-col pb-4 pt-6">
          <div className="mx-5 sm:mx-8 mb-2 border-b border-slate-100 pb-3">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Create account</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Join MedPharma as a licensed doctor. Fields marked optional can be completed later.
            </p>
          </div>
          <div className="mx-5 sm:mx-8 space-y-2">
            <CustomInput
              onChangeText={setFullname}
              value={fullname}
              title="Full name"
              placeholder="e.g. Dr. John Doe"
            />
            <EmailInput
              email={email}
              setEmail={setEmail}
              placeholder="Enter your email address."
            />
            <PasswordInput
              required
              password={password}
              setPassword={setPassword}
              placeholder="Minimum 8 characters"
            />
            <CustomInput
              onChangeText={setPhoneNumber}
              value={phoneNumber}
              title="Phone number"
              placeholder="e.g. +233123456789"
            />
            <div>
              <p className="mb-1 text-sm font-medium text-slate-700">Gender</p>
              <Select
                value={gender}
                onChange={(v) => setGender(v)}
                options={[
                  { value: "male", label: "Male" },
                  { value: "female", label: "Female" },
                ]}
                className="w-full"
                size="large"
                aria-label="Gender"
              />
            </div>
            <CustomInput
              onChangeText={setInstitution}
              value={institution}
              title="Institution"
              placeholder="Your medical institution"
            />
            <CustomInput
              onChangeText={setLicenseId}
              value={licenseId}
              title="License ID"
              placeholder="Medical license ID"
            />
            <CustomInput
              onChangeText={setDob}
              value={dob}
              title="Date of birth (optional)"
              placeholder="YYYY-MM-DD"
            />
            <CustomInput
              onChangeText={setSpeciality}
              value={speciality}
              title="Speciality (optional)"
              placeholder="e.g. Cardiology, Pediatrics"
            />
            <CredentialDocumentsField
              files={pendingCredentialFiles}
              onFilesChange={onCredentialFilesChange}
              onRemove={removeCredentialFile}
              inputRef={credentialInputRefMobile}
            />

            <Button
              type="primary"
              onClick={onSubmit}
              size="large"
              className="!h-12 w-full !mt-4 !rounded-xl !bg-[#1A94EC] !font-semibold shadow-lg shadow-[#1A94EC]/25 hover:!bg-[#1580d4]"
              aria-label="Sign up"
            >
              {loading ? "Creating account…" : "Sign up"}
            </Button>

            <div className="flex justify-center pt-4">
              <p className="text-sm text-slate-500">
                Have an account?{" "}
                <Link to="/sign-in" className="font-semibold text-[#1A94EC] hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
            <div className="mt-4 flex justify-center border-t border-slate-100 pt-3">
              <img src={med} className="h-8" alt="MedPharma" />
            </div>
          </div>
        </div>
      </div>

      <div className="h-screen w-full lg:flex hidden justify-between items-center overflow-y-auto">
        <div
          className="h-full flex flex-col justify-center items-center w-[50%] bg-cover bg-[#0081D4] bg-blend-overlay min-h-0"
          style={{ backgroundImage: `url(${bg})` }}
        >
          <div className="absolute top-6 left-4">
            <img src={med} className="h-8 w-28" alt="MedPharma" />
          </div>
          <div className="flex flex-col justify-center items-center text-center md:w-[70%]">
            <h1 className="text-2xl leading-10 text-white">
              Medpharma's Consultation
            </h1>
            <h1 className="text-2xl leading-10 text-white">
              I have tripled my income as a Specialist.
            </h1>
          </div>
          <div className="my-4 flex flex-col justify-center w-full items-center">
            <div className="h-20 w-20 rounded-full border-4 border-white">
              <img src={doc} alt="" className="h-full w-full rounded-full" />
            </div>
            <h3 className="text-white mt-2 font-medium">Dr. Edwin Mensah</h3>
            <p className="text-[#7ED0FD]">Tamale Teaching Hospital</p>
          </div>
        </div>
        <div className="h-full w-[47%] flex flex-col justify-start items-center py-3 pb-2 overflow-y-auto">
          <div className="w-full max-w-3xl px-8 lg:px-10">
            <div className="mb-3 border-b border-slate-100 pb-3">
              <h1 className="text-4xl font-bold tracking-tight text-slate-900">Create account</h1>
              <p className="mt-2 text-lg text-slate-600 leading-relaxed">
                Register as a doctor to access consultations and prescriptions on MedPharma.
              </p>
            </div>
            <div className="space-y-2 pb-1">
              <CustomInput
                onChangeText={setFullname}
                value={fullname}
                title="Full name"
                placeholder="e.g. Dr. John Doe"
              />
              <EmailInput
                email={email}
                setEmail={setEmail}
                placeholder="Enter your email address."
              />
              <PasswordInput
                required
                password={password}
                setPassword={setPassword}
                placeholder="Minimum 8 characters"
              />
              <CustomInput
                onChangeText={setPhoneNumber}
                value={phoneNumber}
                title="Phone number"
                placeholder="e.g. +233123456789"
              />
              <div>
                <p className="mb-1 text-sm font-medium text-slate-700">Gender</p>
                <Select
                  value={gender}
                  onChange={(v) => setGender(v)}
                  options={[
                    { value: "male", label: "Male" },
                    { value: "female", label: "Female" },
                  ]}
                  className="w-full"
                  size="large"
                  aria-label="Gender"
                />
              </div>
              <CustomInput
                onChangeText={setInstitution}
                value={institution}
                title="Institution"
                placeholder="Your medical institution"
              />
              <CustomInput
                onChangeText={setLicenseId}
                value={licenseId}
                title="License ID"
                placeholder="Medical license ID"
              />
              <CustomInput
                onChangeText={setDob}
                value={dob}
                title="Date of birth (optional)"
                placeholder="YYYY-MM-DD"
              />
              <CustomInput
                onChangeText={setSpeciality}
                value={speciality}
                title="Speciality (optional)"
                placeholder="e.g. Cardiology, Pediatrics"
              />
              <CredentialDocumentsField
                files={pendingCredentialFiles}
                onFilesChange={onCredentialFilesChange}
                onRemove={removeCredentialFile}
                inputRef={credentialInputRefDesktop}
              />

              <Button
                type="primary"
                size="large"
                onClick={onSubmit}
                className="!h-12 w-full !mt-4 !rounded-xl !bg-[#1A94EC] !font-semibold shadow-lg shadow-[#1A94EC]/25 hover:!bg-[#1580d4]"
                aria-label="Sign up"
              >
                {loading ? "Creating account…" : "Sign up"}
              </Button>

              <div className="flex justify-center pt-3">
                <p className="text-base text-slate-500">
                  Have an account?{" "}
                  <Link to="/sign-in" className="font-semibold text-[#1A94EC] hover:underline">
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {contextHolder}
    </div>
  );
}
